import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FaUser, FaList, FaPalette, FaMusic } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface SubDimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[]; // Para compatibilidad
  subDimensions?: SubDimension[];
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}





const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

// Habilidades de Lenguaje Musical (subdimensión 1)
const lenguajeMusicialSkills: Skill[] = [
    {
        "id": 1,
          "name": "Reproduce y reconoce sonidos con cambios de intensidad, altura, duración y timbre ya sea con su propio cuerpo o instrumentos musicales",
        "levels": [
            "Reproduce y reconoce sonidos con cambios de intensidad, altura, duración y timbre ya sea con su propio cuerpo o instrumentos musicales",
            "Reproduce y reconoce sonidos con cambios de intensidad, altura, duración y/o timbre identificando patrones",
            "Reproduce y reconoce sonidos con cambios de intensidad, altura, duración y timbre identificando patrones y secciones",
            "Demuestra conocimiento de los conceptos rítmicos/melódicos, los patrones musicales y el timbre",
            "Demuestra conocimientos de los conceptos rítmicos/melódicos, la forma y la textura en la música",
          "No Aplica",
          "No Aplica",
            "Demuestra un conocimiento de los conceptos rítmicos/melódicos, la forma y la textura, a través del lenguaje, el movimiento y la interpretación",
            "Demuestra conciencia de los conceptos rítmicos/melódicos, la forma y la textura a través del lenguaje, el movimiento y la interpretación"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 2,
          "name": "Explora el ritmo, la dinámica y el tono para comunicar pensamientos, experiencias y sentimientos",
          "levels": [
          "No Aplica",
          "No Aplica",
            "Explora el ritmo, la dinámica y el tono para comunicar pensamientos, experiencias y sentimientos",
            "Utiliza el ritmo, la dinámica y el tono para comunicar pensamientos, experiencias y sentimientos",
            "Explora y usa el color del tono para expresar pensamientos, experiencias y sentimientos",
            "Explora y usa la armonía y la textura para comunicar pensamientos, experiencias y sentimientos",
            "Identifica maneras en que los elementos de la música se utilizan para expresar pensamientos, experiencias y sentimientos en su propio trabajo y en el de otros",
            "No Aplica",
            "No Aplica"
        ],
        "selectedLevelIndex": null
    },
    {
          "id": 3,
          "name": "Escucha con atención diversas músicas expresando sensaciones y emociones",
        "levels": [
            "Escucha con atención diversas músicas expresando sensaciones y emociones",
            "Escucha con atención diversas músicas expresando sensaciones, emociones e identificando instrumentos",
            "Escucha con atención diversas músicas expresando sensaciones, emociones, identificando instrumentos y secciones",
            "Explora diversas maneras de expresar pensamientos, experiencias y sentimientos a través de la música, con énfasis en los materiales",
            "Descubre y experimenta con una variedad de formas de expresar pensamientos, experiencias y sentimientos a través de la música, con énfasis en textos y color del tono",
            "Experimenta con una variedad de formas de comunicar pensamientos, experiencias y sentimientos a través de la música, con énfasis en los estilos de notación",
          "No Aplica",
            "Experimenta con los elementos de la música para crear obras musicales que exploren temas y cuestiones de interés personal",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 4,
          "name": "Entona diversas canciones",
          "levels": [
            "Entona diversas canciones",
            "Entona con buena modulación diversas canciones",
            "Entona con buena modulación diversas canciones y son capaces de cantar la escala de do mayor con fononimias",
            "Contribuye a actividades que exploran el uso creativo de las fuentes sonoras",
            "Explora una variedad de conceptos y formas rítmicos/melódicos para crear, hacer y presentar música",
            "Explora una variedad de materiales y técnicas para crear, hacer y presentar música",
            "Usa materiales, técnicas y formas específicas para crear, hacer y presentar música",
            "Utiliza una variedad de materiales, técnicas y formas para crear, hacer y presentar música",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 5,
          "name": "Improvisa acompañamientos melódicos y rítmicos de ostinato simples",
          "levels": [
          "No Aplica",
          "No Aplica",
          "No Aplica",
            "No Aplica",
            "Improvisa acompañamientos melódicos y rítmicos de ostinato simples",
            "Improvisa acompañamientos ostinatos melódicos y rítmicos simples",
            "Improvisa variaciones rítmicas simples y adornos melódicos simples en melodías familiares",
            "Improvisa canciones cortas y piezas instrumentales utilizando una variedad de fuentes de sonido, incluyendo tradicionales, no tradicionales, corporales y/o electrónicas",
            "Improvisa acompañamientos armónicos simples"
        ],
        "selectedLevelIndex": null
    },
    {
          "id": 6,
          "name": "Juega con instrumentos de percusión y su propio cuerpo con sentido musical",
        "levels": [
            "Juega con instrumentos de percusión y su propio cuerpo con sentido musical",
            "Juega con instrumentos de percusión y su propio cuerpo, atendiendo a indicaciones de volumen",
            "Improvisa patrones rítmicos y melodías sencillas con notas específicas",
            "Improvisa y/o crea melodías con acompañamientos de patrones rítmicos, con intención estética",
            "Interpreta patrones rítmicos y melódicos simples, usando notación estándar o adaptada",
          "No Aplica",
          "No Aplica",
          "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 7,
          "name": "Interpreta canciones cantadas con movimientos corporales libres y/o dirigidos",
          "levels": [
            "Interpreta canciones cantadas con movimientos corporales libres y/o dirigidos",
            "Interpretan canciones cantadas con movimientos corporales libres y/o dirigidos, acompañándose de instrumentos de percusiones menores",
            "Interpretan canciones cantadas con acompañamientos de percusiones menores",
            "Canta solo y en compañía, con énfasis en el tono y la producción",
            "Canta solo y con otros, con énfasis en la dicción y el uso de ostinatos simples",
            "Canta solo y con otros con énfasis en el canto expresivo, la lectura, el fraseo, el rango y texturas más complejas",
            "Canta solo y con otros, con énfasis en el canto expresivo, la lectura, el fraseo, el rango y texturas más complejas",
            "Canta solo y con otros, con énfasis en el canto expresivo de partes, el fraseo, el rango y las texturas más complejas, incluyendo contramelodías y contrapuntos",
            "Canta y toca con otros, con énfasis en la capacidad de mantener su parte dentro de texturas simples, incluyendo repertorio a dos voces"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 8,
          "name": "Reacciona espontáneamente con su cuerpo frente al estímulo rítmico y musical y es capaz de seguir el pulso al caminar",
          "levels": [
            "Reacciona espontáneamente con su cuerpo frente al estímulo rítmico y musical y es capaz de seguir el pulso al caminar",
            "Reproduce patrones de percusión corporal y secuencias de movimiento individual y grupal",
            "Crean patrones rítmicos y secuencias de movimiento individual y en grupo",
            "Improvisa respuestas a frases rítmicas y melódicas dadas",
            "Improvisa acompañamientos rítmicos y melódicos de ostinato simples",
          "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 9,
          "name": "Combina música y movimiento en su creación musical",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Combina música y movimiento en su creación musical",
            "Usa el movimiento para mejorar su creación musical",
            "Interpreta canciones y piezas instrumentales, combinando música y movimiento",
            "No Aplica",
            "No Aplica",
            "Crea e interpreta música que comunique pensamientos, experiencias y sentimientos"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 10,
          "name": "Explora canciones sobre amigos y juegos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora canciones sobre amigos y juegos",
            "Explora canciones sobre el mundo natural",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 11,
          "name": "Escribe, utilizando notación estándar o inventada, un paisaje sonoro",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Escribe, utilizando notación estándar o inventada, un paisaje sonoro",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 12,
          "name": "Crea y presenta un paisaje sonoro",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Crea y presenta un paisaje sonoro",
            "Crea y escribe obras musicales cortas para expresar pensamientos e ideas musicales con énfasis en frases de preguntas y respuestas",
            "Crea y escribe obras musicales cortas para expresar pensamientos e ideas musicales, con énfasis en el motivo y la secuencia",
            "Crea y escribe obras musicales cortas para expresar y comunicar sentimientos personales con énfasis en el tema y las variaciones"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 13,
          "name": "Combina habilidades de lectura y canto/interpretación en su creación musical",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Combina habilidades de lectura y canto/interpretación en su creación musical",
            "Selecciona entre una variedad de materiales, técnicas y formas para crear, hacer y presentar música"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 14,
          "name": "Crea y presenta canciones con acompañamiento rítmico que expresen un significado personal",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Crea y presenta canciones con acompañamiento rítmico que expresen un significado personal",
            "Participa en la creación musical de conjuntos grandes y pequeños",
            "Participa en la creación y presentación musical de conjuntos pequeños y grandes música que refleja diversas imágenes, pensamientos y sentimientos",
            "Participa en la creación musical de conjuntos pequeños y grandes, interpretando un repertorio variado de música"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 15,
          "name": "Lee a primera vista melodías simples de notación tradicional",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Lee a primera vista melodías simples de notación tradicional con énfasis en el movimiento paso a paso y la dinámica",
            "Lee a primera vista melodías simples de notación tradicional, con énfasis en el movimiento salteado y la articulación",
            "Lee a primera vista melodías simples de notación tradicional con énfasis en la secuencia melódica y el fraseo"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 16,
          "name": "Explora la música que encuentran en el colegio y sus propósitos en la vida escolar",
          "levels": [
            "No Aplica",
            "No Aplica",
            "Explora la música que encuentran en el colegio y sus propósitos en la vida escolar",
            "Describe la música que encuentran en el colegio y sus propósitos en la vida escolar",
            "Explora la música que encuentran en la comunidad y los propósitos que cumple en la vida comunitaria",
            "Describe la música que encuentran en su colegio, en casa y en la comunidad y la variedad de propósitos para los que se utiliza",
            "Demuestra conocimientos de los lugares en su comunidad donde pueden aprender y experimentar la música, incluyendo la música de otras culturas",
            "Demuestra conocimiento de los músicos en su comunidad",
            "Participa, describe y compara experiencias musicales en su escuela y comunidad"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 17,
          "name": "Describe sus experiencias de creación musical en su comunidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Describe sus experiencias de creación musical en su comunidad",
            "Describe oportunidades personales para la creación musical en su comunidad, incluyendo oportunidades relacionadas con la cultura popular y los medios de comunicación",
            "Experimenta con estilos, técnicas e instrumentos de una variedad de contextos culturales e históricos al crear, hacer y presentar música",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 18,
          "name": "Identifica, describe y compara instrumentos de una variedad de contextos culturales e históricos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Identifica, describe y compara instrumentos de una variedad de contextos culturales e históricos",
            "Identifica, describe y compara estilos musicales de diversos contextos culturales e históricos",
            "Usa una variedad de formas musicales para dar significado a eventos y problemas culturales e históricos de Chile",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 19,
          "name": "Explora canciones y juegos musicales de la cultura",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora canciones y juegos musicales de la cultura",
            "Compara la música de varias culturas",
            "Demuestra respeto por la música y los músicos de diversas culturas",
            "No Aplica",
            "Demuestra conocimiento de las convenciones del comportamiento del público en diversos contextos de interpretación",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 20,
          "name": "Debate sobre música y músicos de diversas culturas",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Debate sobre música y músicos de diversas culturas",
            "Explora juegos de canto de una variedad de culturas",
            "Explora la música vocal e instrumental de diversas culturas",
            "Usa su conocimiento y experiencia para respetar y valorar las contribuciones musicales de los grupos culturales en su propia comunidad",
            "Usa su conocimiento y experiencia para respetar y valorar las contribuciones de los grupos culturales en Chile",
            "Usa sus conocimientos y experiencia para interpretar y respetar la música de diversos grupos culturales"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 21,
          "name": "Explora instrumentos de diversas culturas",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora instrumentos de diversas culturas",
            "Explora instrumentos de una variedad de culturas, incluidos los que se encuentran en la música de Chile",
            "Explora y hace conexiones entre la cultura y la música de Chile",
            "Explora el papel que juega la música en la cultura de Chile",
            "Explora el papel que desempeña la música en las culturas de América",
            "Explora el papel que desempeña la música en las culturas de diferentes partes del mundo"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 22,
          "name": "Utiliza la música para describir experiencias personales",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Utiliza la música para describir experiencias personales",
            "Usa la música y las imágenes visuales para interpretar su mundo",
            "Explora las formas en que la música expresa y mejora sus experiencias de vida",
            "Explora el trabajo de varios compositores y músicos y su contribución a la sociedad, pasada y presente",
            "Examina las contribuciones de diversos compositores y músicos, pasados y presentes, a su sociedad",
            "Demuestra una comprensión del poder de la música, pasada y presente, para expresar y comunicar ideas y sentimientos"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 23,
          "name": "Explora las influencias culturales en la música de su comunidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora las influencias culturales en la música de su comunidad",
            "Explora y hace conexiones entre la cultura y la música",
            "Explora una variedad de influencias en la música que ellos y otros crean y presentan",
            "Explora una variedad de formas en que la música puede usarse para reflejar temas e ideas",
            "Explora y describe la relación entre la música y los eventos y problemas locales",
            "Examina el papel que juega la música en la cultura popular y los medios de comunicación"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 24,
          "name": "Utiliza la música y el juego de roles para interpretar su mundo",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Utiliza la música y el juego de roles para interpretar su mundo",
            "Explora las formas en que sus propias vidas y circunstancias influyen en la música que hacen y crean",
            "Explora y hace conexiones entre las circunstancias personales de los compositores y sus obras musicales",
            "Explora e identifica la relación entre el trabajo/entorno laboral y la música, pasado y presente",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 25,
          "name": "Explora las conexiones entre la música y otras disciplinas artísticas",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora las conexiones entre la música y otras disciplinas artísticas",
            "Identifica conexiones entre la música y el movimiento, incluyendo el teatro y la danza",
            "Identifica similitudes y diferencias entre la música y las artes visuales",
            "Identifica y describe las conexiones entre la música y otras áreas curriculares"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 26,
          "name": "Expresa y comunica pensamientos, experiencias y sentimientos a través de la música y las imágenes visuales",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Expresa y comunica pensamientos, experiencias y sentimientos a través de la música y las imágenes visuales",
            "Expresa y comunica sentimientos personales a través de la música y el lenguaje escrito y hablado"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 27,
          "name": "Reproduce rítmicamente: negra, doble corchea y silencios, con cambios de intensidad",  
          "levels": [
            "Reproduce rítmicamente: negra, doble corchea y silencios, con cambios de intensidad",
            "Reproducen rítmicamente negra, doble corchea, cuartina y silencios, con cambios de intensidad",
            "Reproducen rítmicamente negra, doble, cuartina y silencio, reconociendo su equivalencia en la escritura musical",
            "Responde a través del movimiento a melodías simples, con énfasis en alto/bajo, igual/diferente, ritmo y canto afinado",
            "Responde a la música con énfasis en la métrica, la dirección melódica, el timbre y el canto afinado",
            "Responde a la música con énfasis en el tono, la articulación, la textura, el timbre y el canto afinado",
            "No Aplica",
            "Aplica el conocimiento de la música para tomar decisiones individuales basadas en los pensamientos, imágenes y sentimientos que la música expresa",
            "Analiza soluciones musicales para tomar decisiones informadas basadas en los pensamientos, imágenes y sentimientos que expresa la música"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 28,
          "name": "Describe sonidos igual/diferente, largo/corto",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Describe sonidos igual/diferente, largo/corto",
            "Describe su propia creación musical y la de otros con énfasis en el ritmo, el tempo, la dinámica, lo alto/bajo y lo mismo/diferente",
            "Describe su propia creación musical y la de otros, con énfasis en el tono, la articulación, la textura y el timbre",
            "Identifica problemas y posibles soluciones en el proceso de creación musical",
            "No Aplica",
            "Analiza y toma decisiones sobre diversas interpretaciones de una obra"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 29,
          "name": "Explora posibilidades y toma decisiones durante el proceso de creación musical",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora posibilidades y toma decisiones durante el proceso de creación musical",
            "Explora diferentes soluciones y toma decisiones durante el proceso de creación musical",
            "Utiliza criterios musicales para evaluar el desempeño del repertorio del aula, con énfasis en la melodía y la armonía",
            "Compara la forma y los principios del diseño en la estructura rítmica/melódica del repertorio del aula",
            "Compara respuestas subjetivas y objetivas a la música y examina la relación entre ellas"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 30,
          "name": "Reconoce por la vista y el sonido los instrumentos de aula de uso común",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Demuestra conciencia de que los instrumentos del aula pueden producir una variedad de sonidos, como alto/bajo, largo/corto",
            "Reconoce por la vista y el sonido los instrumentos de aula de uso común",
            "Identifica fuentes de sonido y sus efectos expresivos",
            "No Aplica",
            "Reconoce por la vista y el sonido, y categoriza por familia, instrumentos orquestales, de banda y de teclado",
            "Reconoce por la vista y el sonido, y categoriza por familia, instrumentos orquestales y de teclado de diversas culturas"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 31,
          "name": "Explora razones para hacer música en la casa, colegio y/o la comunidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora razones para hacer música en la casa, colegio y/o la comunidad",
            "No Aplica",
            "Comprende que hay muchas razones para hacer música",
            "Utiliza el conocimiento de los elementos musicales para describir la música que escuchan",
            "Usa criterios musicales para evaluar su capacidad de mantener una parte melódica/armónica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 32,
          "name": "Reconoce instrumentos orquestales y de teclado comunes por la vista y el sonido",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Reconoce instrumentos orquestales y de teclado comunes por la vista y el sonido",
            "Usa el conocimiento de los elementos musicales para comparar y contrastar música de varios géneros",
            "Descubre las razones por las que se han creado obras musicales específicas"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 33,
          "name": "Comparte ideas y sentimientos con otros durante su creación musical",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Comparte ideas y sentimientos con otros durante su creación musical",
            "Comparte ideas y sentimientos con otros durante su creación musical",
            "Comparte ideas y sentimientos sobre las respuestas de los demás a la música",
            "Demuestra respeto por las respuestas de los demás a la música",
            "Explora diversas influencias en los compositores y sus obras"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 34,
          "name": "Habla sobre sus razones para hacer y crear música",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Habla sobre sus razones para hacer y crear música",
            "Describe de diversas maneras sus razones para crear una pieza musical en particular",
            "No Aplica",
            "Describe las razones de sus decisiones musicales",
            "Comunica la fuente de ideas y las razones de sus propias decisiones musicales"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 35,
          "name": "Demuestra confianza al compartir ideas y sentimientos con otros durante su creación musical",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Demuestra confianza al compartir ideas y sentimientos con otros durante su creación musical",
            "Investiga la fuente de ideas para la música que escuchan y hacen",
            "Compara interpretaciones de obras musicales utilizando la terminología apropiada",
            "Compara interpretaciones de sus propias y la música de otros, utilizando la terminología apropiada y considerando la intención musical"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 36,
          "name": "Explora su propio trabajo musical a la luz de lo que pretendían",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Explora su propio trabajo musical a la luz de lo que pretendían",
            "Examina sus presentaciones grupales a la luz de lo que pretendían",
            "Reflexiona sobre su creación musical a la luz de lo que se pretendía mediante el uso de las tecnologías de grabación disponibles"
        ],
        "selectedLevelIndex": null
      }
    ];

// Habilidades de Lenguaje Visual (subdimensión 2)
// Los IDs empiezan desde 37 para no superponerse con Lenguaje Musical (que va hasta 36)
const lenguajeVisualSkills: Skill[] = [
  {
    "id": 37,
    "name": "Comunica oralmente sus impresiones y reflexiones respecto de obras de arte, composiciones o producciones propias o de sus pares detallando si le gusta o no le gusta con la mediación del adulto.",
    "levels": [
      "Comunica oralmente sus impresiones y reflexiones respecto de obras de arte, composiciones o producciones propias o de sus pares detallando si le gusta o no le gusta con la mediación del adulto.",
      "Comunica oralmente sus impresiones y reflexiones respecto de obras de arte, composiciones o producciones propias o de sus pares detallando si le gusta o no le gusta y qué o porqué, con la mediación del adulto.",
      "Comunica oralmente sus impresiones y reflexiones respecto de obras de arte, composiciones o producciones propias o de sus pares justificando su respúesta con la mediación del adulto.",
      "Expresa y crea trabajos de arte a partir de la observación de elementos y familias cercanas (vida cotidiana, animales, plantas, obras de arte local, etc)",
      "Expresa y crea trabajos de arte a partir de la observación de elementos del país y figuras humanas (obras de arte chilenos, personajes, patrimonio de la cultura, etc.)",
      "No Aplica",
      "Crea trabajos de arte con un propósito expresivo personal basado en la observación (animales, fenómenos naturales, mitos, tradiciones, arte de la antigüedad, etc.)",
      "No Aplica",
      "Crea trabajos de arte y diseños a partir de sus propias ideas y de la observación del entorno cultural y artístico."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 38,
    "name": "Crea composiciones simples siendo capaz de responder preguntas descriptivas",
    "levels": [
      "Crea composiciones simples siendo capaz de responder preguntas descriptivas",
      "Crea composiciones siendo capaz de explicar lo que ésta representa con ayuda del adulto.",
      "Crea composiciones siendo capaz de explicar lo que ésta representa.",
      "Crea intentando expresar sus emociones e ideas en sus trabajos de arte a partir de la experimentación.",
      "Expresa sus emociones e ideas en sus trabajos de arte a partir de la experimentación.",
      "Crea, con la ayuda del profesor, trabajos de arte a partir de experiencias, intereses y temas del entorno natural y artístico.",
      "Crea trabajos de arte a partir de experiencias, intereses y temas del entorno natural y artístico.",
      "Crea trabajos y diseños a partir de diferentes desafíos y temas del entorno cultural y artístico, pidiendo ayuda si lo necesita.",
      "Crea trabajos y diseños a partir de diferentes desafíos y temas del entorno cultural y artístico."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 39,
    "name": "Observa y/o escucha las propuestas, provocaciones o inspiraciones que se le ofrecen.",
    "levels": [
      "Observa y/o escucha las propuestas, provocaciones o inspiraciones que se le ofrecen.",
      "Explora diferentes materiales y el efecto que tiene la interacción intencionada entre ellos o por sí solos.",
      "Describe lo que observa o escucha en su entorno o en producciones artísticas con la ayuda del adulto.",
      "Comunica oralmente sus impresiones de lo que siente y piensa de algunas obras de arte, siendo apoyado por el adulto.",
      "Comunica oralmente sus impresiones de lo que siente y piensa de algunas obras de arte.",
      "Describe sus observaciones de obras de arte y objetivos, usando elementos del lenguaje visual, expresando lo que siente y/o piensa; pidiendo ayuda si lo necesita.",
      "Describe sus observaciones de obras de arte y objetivos, usando elementos del lenguaje visual, expresando lo que siente y piensa.",
      "Analiza e interpreta obras de arte y diseño en relación con la aplicación del lenguaje visual, contextos, materiales, estilos, etc., pidiendo ayuda si lo necesita.",
      "Analiza e interpreta obras de arte y diseño en relación con la aplicación del lenguaje visual, contextos, materiales, estilos, etc."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 40,
    "name": "Explica sus preferencias frente al trabajo de arte personal y de sus pares, usando elementos del lenguaje visual.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Explica sus preferencias frente al trabajo de arte personal y de sus pares, usando elementos del lenguaje visual.",
      "No Aplica",
      "Describe fortalezas y aspectos a mejorar en el trabajo de arte personal y de sus pares, aplicando criterios de uso de materiales, procedimiento técnico, propósito expresivo, etc.",
      "Describe y compara trabajos de arte y diseños personales y de sus pares, consierando própisto expresico, material, fortalezas y que se puede mejorar, etc.",
      "Evalúa críticamente trabajos de arte y diseño personales y de sus pares considerando la expresión e emociones, uso del material, propósitos expresivos, etc."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 41,
    "name": "Dibuja o modela figura humana o animal incompleta (cabeza, manos, pies)",
    "levels": [
      "Dibuja o modela figura humana o animal incompleta (cabeza, manos, pies)",
      "Dibuja o modela figura humana o anima completa (cabeza, tronco, brazos, manos, piernas, pies. Puede incluir partes del rostro: ojos, nariz, boca, orejas)",
      "Dibuja o modela figura humana completa y vestida, con detalles como cejas, vestido, zapatos, dedos, etc.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 42,
    "name": "Experimenta y aplica en sus trabajos diferentes líneas como gruesas, delgadas, rectas, onduladas e irregulares.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Experimenta y aplica en sus trabajos diferentes líneas como gruesas, delgadas, rectas, onduladas e irregulares.",
      "Experimenta y aplica en sus trabajos diferentes líneas como certicales, horizontales, diagonales, espirales y quebrada.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 43,
    "name": "Reconoce los colores y los puede nombrar.",
    "levels": [
      "Reconoce los colores y los puede nombrar.",
      "No Aplica",
      "Reconoce colores primarios y secundarios.",
      "Reconoce, aplica y explora colores primarios y secundarios.",
      "Explora el uso del blanco y el negro como pigmento para hacer escalas de color.",
      "Aplica colores fríos, cálidos y expresidos.",
      "Aplica en sus trabajos utilizando colores (tono y matiz)",
      "Aplica y combina en sus trabajos de arte y diseño utilizando colores complementarios.",
      "Aplica y combina en sus trabajos de arte y diseño utilizando colores, sus gamas y contrastes"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 44,
    "name": "Experimenta y aplica en sus trabajos texturas visuales y táctiles.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Experimenta y aplica en sus trabajos texturas visuales y táctiles.",
      "No Aplica",
      "Aplica en sus trabajos de arte texturas en plano y volumen.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 45,
    "name": "Experimenta y aplica en sus trabajos formas diferentes como las geométricas.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Experimenta y aplica en sus trabajos formas diferentes como las geométricas.",
      "Aplica en sus trabajos de arte formas reales y recreadas.",
      "Aplica en sus trabajos utilizando formas figurativas y no figurativas.",
      "Aplica y combina en sus trabajos de arte y diseño utilizando formas abiertas y cerradas.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 46,
    "name": "Aplica y combina en sus trabajos de arte y diseño utilizando la luz y la sombra.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Aplica y combina en sus trabajos de arte y diseño utilizando la luz y la sombra.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  }
]
;

// Combinar ambas subdimensiones para compatibilidad
const allArtisticSkills: Skill[] = [...lenguajeMusicialSkills, ...lenguajeVisualSkills];

// Definición de la dimensión con datos hardcodeados y subdimensiones
export const lenguajesArtisticosDimension: Dimension = {
  id: 5,
  name: "Lenguaje Artístico",
  skills: allArtisticSkills, // Para compatibilidad
  subDimensions: [
    {
      id: 1,
      name: "Lenguaje Musical",
      skills: lenguajeMusicialSkills
    },
    {
      id: 2,
      name: "Lenguaje Visual",
      skills: lenguajeVisualSkills
    }
  ]
};



const LenguajeArtistico: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [showAllYears, setShowAllYears] = useState(true);
  
  // Estado para manejar las pestañas de subdimensiones
  const [activeSubDimension, setActiveSubDimension] = useState<number>(1); // 1 = Lenguaje Musical, 2 = Lenguaje Visual

  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};

      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === lenguajesArtisticosDimension.id && skillLevel.level !== null && skillLevel.level !== undefined) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });

      setPreviouslySelectedLevels(initialLevels);
    }
  }, [students]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");

  // Función para obtener las habilidades de la subdimensión activa
  const getCurrentSubDimensionSkills = (): Skill[] => {
    const subDimension = lenguajesArtisticosDimension.subDimensions?.find(sub => sub.id === activeSubDimension);
    return subDimension ? subDimension.skills : lenguajeMusicialSkills;
  };

  // useEffect para manejar la selección por defecto
  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId && getCurrentSubDimensionSkills().length > 0) {
      const firstSkill = getCurrentSubDimensionSkills()[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, activeSubDimension]);

  // useEffect para cambiar a la primera habilidad cuando se cambia de subdimensión
  useEffect(() => {
    if (viewMode === 'skill') {
      const currentSkills = getCurrentSubDimensionSkills();
      if (currentSkills.length > 0) {
        // Verificar si la habilidad actual pertenece a la subdimensión activa
        const currentSkillExists = currentSkills.find(skill => skill.id === selectedSkillId);
        if (!currentSkillExists) {
          setSelectedSkillId(currentSkills[0].id);
        }
      } else {
        setSelectedSkillId(null);
      }
    }
  }, [activeSubDimension, viewMode, selectedSkillId]);

  // Función para obtener el nivel seleccionado de una habilidad específica de un estudiante
  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === lenguajesArtisticosDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === lenguajesArtisticosDimension.id && skillLevel.level !== null && skillLevel.level !== undefined) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students]);

  // Función auxiliar para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  // Memoizar estudiantes filtrados con búsqueda y orden
  const filteredStudents = useMemo(() => {
    let filtered = selectedCourse
      ? students.filter(student => student.course === selectedCourse)
      : students;

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchLower) ||
        student.course.toLowerCase().includes(searchLower)
      );
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "lastName":
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        case "course":
          return a.course.localeCompare(b.course);
        default:
          return 0;
      }
    });

    return filtered;
  }, [students, selectedCourse, searchTerm, sortBy, getFirstLastName]);

  // Función para obtener el color según el porcentaje de completado
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  // Función para obtener el color del texto del porcentaje
  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'text-white opacity-75';
    }
    
    if (percentage >= 75) {
      return 'text-green-600 opacity-90';
    } else if (percentage >= 50) {
      return 'text-blue-600 opacity-90';
    } else if (percentage >= 25) {
      return 'text-yellow-600 opacity-90';
    } else {
      return 'text-red-600 opacity-90';
    }
  };



  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, lenguajesArtisticosDimension.id, skillId);

      // Actualizar previouslySelectedLevels si no existe o si cambió
      const currentPrevLevel = previouslySelectedLevels?.[`${studentId}-${skillId}`];
      if (currentLevel !== currentPrevLevel) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, lenguajesArtisticosDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, lenguajesArtisticosDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  const selectColumnForAllStudents = (levelIndex: number, students: Student[], selectedSkillId: number, handleLevelChange: Function) => {
    // Verificar que la habilidad seleccionada pertenezca a la subdimensión activa
    const currentSubDimensionSkills = getCurrentSubDimensionSkills();
    const skillBelongsToCurrentSubDimension = currentSubDimensionSkills.some(skill => skill.id === selectedSkillId);

    if (!skillBelongsToCurrentSubDimension) {
      console.warn('La habilidad seleccionada no pertenece a la subdimensión activa');
      return;
    }

    students.forEach(student => {
      const currentLevel = student.skillLevels.find(
        sl => sl.dimensionId === lenguajesArtisticosDimension.id && sl.skillId === selectedSkillId
      )?.level;

      if (currentLevel === levelIndex) {
        handleLevelChange(student.id as any, lenguajesArtisticosDimension.id, selectedSkillId, null);
      } else {
        // Permitir seleccionar "No aplica" si no está seleccionado actualmente
        handleLevelChange(student.id as any, lenguajesArtisticosDimension.id, selectedSkillId, levelIndex);
      }
    });
  };

  const selectColumnForAllSkills = (levelIndex: number, studentId: number | string, handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, level: number | null) => void) => {
    // Usar solo las habilidades de la subdimensión activa
    const currentSubDimensionSkills = getCurrentSubDimensionSkills();
    currentSubDimensionSkills.forEach(skill => {
      const currentLevel = students.find(s => s.id === studentId)?.skillLevels.find(
        sl => sl.dimensionId === lenguajesArtisticosDimension.id && sl.skillId === skill.id
      )?.level;

      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, lenguajesArtisticosDimension.id, skill.id, null);
      } else {
        // Permitir seleccionar "No aplica" si no está seleccionado actualmente
        handleLevelChange(studentId, lenguajesArtisticosDimension.id, skill.id, levelIndex);
      }
    });
  };

  // Obtener habilidades de la subdimensión activa para usar en los renders
  const currentSkills = getCurrentSubDimensionSkills();

  const renderStudentView = () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    // Generar array completo de años (4-12)
    const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
    
    // Filtrar años según la configuración
    const getVisibleYears = () => {
      if (showAllYears) {
        return allYears;
      }
      
      // Mostrar edad ±2 años (mínimo 4 años, máximo 12)
      const studentAge = age;
      const minAge = Math.max(4, studentAge - 2);
      const maxAge = Math.min(12, studentAge + 2);
      
      return allYears.filter((_, index) => {
        const yearAge = index + 4;
        return yearAge >= minAge && yearAge <= maxAge;
      });
    };
    
    const visibleYears = getVisibleYears();
    const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      return (
        <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            {/* Indicador de progreso */}
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                  <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                    {(() => {
                      // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                      const progress = calculateFormProgress(
                        lenguajesArtisticosDimension.id,
                        student.course,
                        student.skillLevels
                      );
                      const progressPercent = progress.percentage;
                      
                      let barColor = "bg-red-500";
                      if (progressPercent >= 75) barColor = "bg-green-500";
                      else if (progressPercent >= 50) barColor = "bg-blue-500";
                      else if (progressPercent >= 25) barColor = "bg-yellow-500";
                      
                      return (
                        <div 
                          className={`h-2.5 rounded-full ${barColor}`} 
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">
                    {(() => {
                      const progress = calculateFormProgress(
                        lenguajesArtisticosDimension.id,
                        student.course,
                        student.skillLevels
                      );
                      return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                    })()}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                        currentSkills.forEach(skill => {
                          // Pasar un valor para level que será interpretado como null
                          handleLocalLevelChange(student.id as any, skill.id, -1);
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Reiniciar
                  </button>
                </div>
              </div>
            </div>

            {/* Información del estudiante */}
            <div className="bg-indigo-50 p-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-indigo-900">{student.name}</h3>
                  <p className="text-sm text-indigo-700">{student.course} • {age} años</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-indigo-600">
                    {showAllYears ? 'Mostrando: Todos los años (4-12)' : `Mostrando: Edad ±2 años (${Math.max(4, age-2)}-${Math.min(12, age+2)})`}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index]; // Obtener el índice real del nivel
                      return (
                    <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                        <button
                              onClick={() => selectColumnForAllSkills(actualIndex, student.id as any, handleLevelChange)}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                        >
                          Seleccionar todo
                        </button>
                      </div>
                    </th>
                      );
                    })}
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {currentSkills.map((skill: Skill, skillIndex: number) => {
                      const selectedLevel = getSkillLevel(student, lenguajesArtisticosDimension.id, skill.id);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, lenguajesArtisticosDimension.id, skill.id);
                      
                      const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                      return (
                      <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                              {skillIndex + 1}
                            </span>
                            <div className="flex-1">
                              {skill.name}
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, lenguajesArtisticosDimension.id, skill.id, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null; // Si no existe el nivel para este índice, no mostrar nada
                          
                          // Determinar si este nivel "no aplica" basado en el texto
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                          
                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                          
                          const expectedLevel = getExpectedLevel(age);

                          const isExpected = levelIndex === expectedLevel;
                          const isPrevious = levelIndex === previousYearLevel;

                          // 1. Fondo según selección o estado base
                          if (isNotApplicable) {
                            // Si es "No aplica", verificar si está seleccionado
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex || (previouslySelected && previouslySelectedLevels?.[`${student.id}-${skill.id}`] === levelIndex)) {
                            buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            // Mostrar nivel previamente seleccionado
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else {
                            buttonClass += LEVEL_COLORS.default;
                          }

                          // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                          let extraStyles: React.CSSProperties = {
                            boxSizing: 'border-box'
                          };

                          if (isPrevious) {
                            // Aplicar borde gris grueso para nivel año anterior
                            extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                            if (selectedLevel !== levelIndex) {
                              extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                            }
                          }

                          if (isExpected) {
                            // Si es esperado, aplicar borde amarillo
                            if (!isPrevious) {
                              extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                              }
                            }
                          }

                          return (
                            <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                              <button
                                onClick={() => {
                                  if (selectedLevel === levelIndex) {
                                    // Si ya está seleccionado, deseleccionar
                                    handleLocalLevelChange(student.id as any, skill.id, null);
                                  } else {
                                    // Si no está seleccionado, seleccionar
                                    handleLocalLevelChange(student.id as any, skill.id, levelIndex);
                                  }
                                }}
                                className={buttonClass}
                                style={extraStyles}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                              >
                                <span className="text-center leading-tight">{level}</span>
                              </button>
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
          </div>
        </div>
      );
    });

  const renderSkillView = () => {
    if (!selectedSkillId) return <div />;

    const allStudentsNotEvaluated =
      filteredStudents.length > 0 &&
      filteredStudents.every(student =>
        isSkillNotEvaluated(student, lenguajesArtisticosDimension.id, selectedSkillId)
      );

    const toggleNotEvaluatedForAll = () => {
      if (!handleNotEvaluatedChange) return;
      const nextState = !allStudentsNotEvaluated;
      filteredStudents.forEach(student => {
        handleNotEvaluatedChange(
          student.id,
          lenguajesArtisticosDimension.id,
          selectedSkillId,
          nextState
        );
      });
    };

    return (
      <div>
        <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            {/* Header de la tabla para skill view */}
            <div className="bg-indigo-50 p-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-indigo-900">
                    {currentSkills.find(s => s.id === selectedSkillId)?.name}
                  </h3>
                  <p className="text-sm text-indigo-700">Vista por habilidad</p>
                </div>
                <div className="text-right">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated
                              ? "✓ Habilitar evaluación"
                              : "🚫 No evaluar esta habilidad"}
                          </button>
                        )}
                      </div>
                    </th>
                    {(() => {
                      // Generar array completo de años (4-12)
                      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
                      
                      // Si hay estudiantes seleccionados, usar la edad del primero para el filtro
                      const firstStudent = filteredStudents[0];
                      let visibleYears = allYears;
                      let visibleIndices = Array.from({length: 9}, (_, i) => i);
                      
                      if (!showAllYears && firstStudent) {
                        const age = deriveAnchoredAge({ course: firstStudent.course, birthDate: firstStudent.birthDate, preferCourse: true });
                        
                        // Filtrar años según la edad ±2
                        const minAge = Math.max(4, age - 2);
                        const maxAge = Math.min(12, age + 2);
                        
                        visibleYears = allYears.filter((_, index) => {
                          const yearAge = index + 4;
                          return yearAge >= minAge && yearAge <= maxAge;
                        });
                        
                        visibleIndices = visibleYears.map((year, index) => {
                          const match = year.match(/\d+/);
                          const yearNum = match ? parseInt(match[0]) : 4;
                          const calculatedIndex = yearNum - 4;
                          // Asegurar que nunca sea NaN y usar fallback con index
                          return isNaN(calculatedIndex) ? index : calculatedIndex;
                        });
                      }
                      
                      return visibleYears.map((ageGroup, index) => (
                    <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                      <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                        <button
                              onClick={() => selectColumnForAllStudents(visibleIndices[index], filteredStudents, selectedSkillId, handleLevelChange)}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                        >
                          Seleccionar todo
                        </button>
                      </div>
                    </th>
                      ));
                    })()}
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student, studentIndex) => {
                    const selectedLevel = getSkillLevel(student, lenguajesArtisticosDimension.id, selectedSkillId);
                    const skillIsNotEvaluated = isSkillNotEvaluated(student, lenguajesArtisticosDimension.id, selectedSkillId);
                    
                    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

                    // Calcular años visibles para este estudiante (igual que en el header)
                    const allYears = Array.from({length: 9}, (_, i) => i); // Índices 0-8
                    let visibleIndices = allYears;
                    
                    if (!showAllYears) {
                      const minAge = Math.max(4, age - 2);
                      const maxAge = Math.min(12, age + 2);
                      
                      visibleIndices = allYears.filter(index => {
                        const yearAge = index + 4;
                        return yearAge >= minAge && yearAge <= maxAge;
                      });
                    }
                    
                    return (
                      <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                              {studentIndex + 1}
                            </span>
                            <div className="flex-1">
                              <div className="font-semibold">{student.name}</div>
                              <div className="text-xs text-gray-500">{student.course} • {age} años</div>
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, lenguajesArtisticosDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = currentSkills
                            .find(skill => skill.id === selectedSkillId)?.levels[levelIndex];
                          
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${selectedSkillId}`] === levelIndex;

                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';

                          const previousYearLevel = getHistoricalLevel(student, selectedSkillId!, age);
                          const expectedLevel = getExpectedLevel(age);

                          if (isNotApplicable) {
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex || (previouslySelected && previouslySelectedLevels?.[`${student.id}-${selectedSkillId}`] === levelIndex)) {
                              buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else if (levelIndex === expectedLevel) {
                              buttonClass += LEVEL_COLORS.expected;
                          } else if (levelIndex === previousYearLevel) {
                              buttonClass += LEVEL_COLORS.previous;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            return (
                            <td key={`skill-${selectedSkillId}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                onClick={() => {
                                  if (selectedLevel === levelIndex) {
                                    // Si ya está seleccionado, deseleccionar
                                    handleLocalLevelChange(student.id as any, selectedSkillId, null);
                                  } else {
                                    // Si no está seleccionado, seleccionar
                                    handleLocalLevelChange(student.id as any, selectedSkillId, levelIndex);
                                  }
                                }}
                                  className={buttonClass}
                                title={`Nivel ${levelIndex + 1}: ${level}`}
                                >
                                <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            </div>
          </div>
          </div>
        </div>
    );
  };

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const progress = calculateFormProgress(
                  lenguajesArtisticosDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥75% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥50% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥25% completado,</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }
    
      return (
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Buscar habilidad..." 
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
            
            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Número</option>
                <option value="course">% Completado</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {currentSkills
            .filter(skill => 
              searchTerm === '' || 
              skill.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
              if (sortBy === 'name') return a.name.localeCompare(b.name);
              if (sortBy === 'lastName') return a.id - b.id; // Por número
              if (sortBy === 'course') { // Por % completado
                const aCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, lenguajesArtisticosDimension.id, a.id) !== null
                ).length;
                const bCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, lenguajesArtisticosDimension.id, b.id) !== null
                ).length;
                const aPercentage = filteredStudents.length > 0 ? (aCompleted / filteredStudents.length) * 100 : 0;
                const bPercentage = filteredStudents.length > 0 ? (bCompleted / filteredStudents.length) * 100 : 0;
                return bPercentage - aPercentage;
              }
              return 0;
            })
            .map((skill) => {
              const completed = filteredStudents.filter(student => 
                getSkillLevel(student, lenguajesArtisticosDimension.id, skill.id) !== null
              ).length;
              const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
              
              return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(percentage, selectedSkillId === skill.id)
                  }`}
                >
                  <div className="font-semibold" title={skill.name}>
                {skill.name}
                  </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
              </button>
              );
            })}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥75% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥50% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥25% completado,</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Busque y ordene para encontrar habilidades específicas
            </span>
          </div>
          </div>
        </div>
      );
  };

  // Solo mostrar el componente si las habilidades están cargadas
      if (allArtisticSkills.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
        <p className="text-lg text-gray-600">Cargando habilidades de Lenguaje Artístico...</p>
        <p className="text-sm text-gray-500 mt-2">Procesando archivo lenguaje_musical_2025.csv</p>
      </div>
    );
  }

  // Mostrar mensaje si no se cargaron habilidades
      if (allArtisticSkills.length === 0) {
    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Error cargando habilidades
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>No se pudieron cargar las habilidades desde el archivo CSV <code>lenguaje_musical_2025.csv</code>.</p>
              <p className="mt-1">Usando datos de respaldo. Por favor, verifique el archivo CSV.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

          return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={lenguajesArtisticosDimension.name}
        icon={FaPalette}
        skillsCount={getCurrentSubDimensionSkills().length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
      
      {/* Pestañas de Subdimensiones */}
      <div className="mt-4 mb-4">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-lg">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveSubDimension(1)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 1
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaMusic className="mr-2 text-sm" />
              <span className="font-medium">Lenguaje Musical</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {lenguajeMusicialSkills.length}
              </span>
            </button>
            
            <button
              onClick={() => setActiveSubDimension(2)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 2
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaPalette className="mr-2 text-sm" />
              <span className="font-medium">Lenguaje Visual</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {lenguajeVisualSkills.length}
              </span>
            </button>
          </div>
        </div>
      </div>
        
      {/* Botones de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleViewModeChange('student')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'student' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>
            
            <button
              onClick={() => handleViewModeChange('skill')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'skill' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>

      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo (hasta 12 años):
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 shadow-sm border"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel seleccionado</span> - Nivel evaluado para el estudiante
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel esperado</span> - Nivel correspondiente a la edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel año anterior</span> - Nivel del año previo
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Previamente seleccionado</span> - Nivel anterior guardado
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2 hover:bg-blue-50"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Sin seleccionar</span> - Nivel disponible para evaluar
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 border flex items-center justify-center">
                  <span className="text-xs text-gray-400 italic">N/A</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No aplica</span> - No corresponde para esta edad
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-500 italic">
                💡 <strong>Instrucciones:</strong> Haz clic en los niveles para seleccionar el desarrollo actual del estudiante. 
                Los colores te ayudan a identificar el progreso esperado según la edad (4-12 años).
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LenguajeArtistico;